// ============================================
// KPI Card Component
// ============================================

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { KPIValue, Trend } from '../../types/api';
import { formatCurrency, formatNumber, formatPercent, formatPercentPlain, formatROAS } from '../../utils/formatters';
import { getTrendColor } from '../../utils/colors';

type FormatType = 'currency' | 'number' | 'percent' | 'roas';
type MetricType = 'growth' | 'cost';

interface KPICardProps {
  title: string;
  data: KPIValue;
  format?: FormatType;
  metricType?: MetricType;
  subtitle?: string;
  icon?: React.ReactNode;
  loading?: boolean;
}

function formatValue(value: number, format: FormatType): string {
  switch (format) {
    case 'currency':
      return formatCurrency(value);
    case 'percent':
      return formatPercentPlain(value);
    case 'roas':
      return formatROAS(value);
    default:
      return formatNumber(value);
  }
}

function TrendIcon({ trend }: { trend: Trend }) {
  switch (trend) {
    case 'up':
      return <TrendingUp className="w-4 h-4" />;
    case 'down':
      return <TrendingDown className="w-4 h-4" />;
    default:
      return <Minus className="w-4 h-4" />;
  }
}

export function KPICard({
  title,
  data,
  format = 'number',
  metricType = 'growth',
  subtitle,
  icon,
  loading = false,
}: KPICardProps) {
  const trendColor = getTrendColor(data.trend, metricType);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-20"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 hover:shadow-lg hover:shadow-gray-100 transition-all duration-300 cursor-pointer">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs sm:text-sm font-medium text-gray-500">{title}</span>
        {icon && <span className="text-gray-400">{icon}</span>}
      </div>

      {/* Main Value */}
      <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
        {formatValue(data.value, format)}
      </div>

      {/* Trend Indicator */}
      <div className="flex items-center gap-1 flex-wrap" style={{ color: trendColor }}>
        <TrendIcon trend={data.trend} />
        <span className="text-xs sm:text-sm font-medium">
          {formatPercent(data.change)}
        </span>
        <span className="text-xs sm:text-sm text-gray-500 ml-1">vs kỳ trước</span>
      </div>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-xs text-gray-400 mt-2">{subtitle}</p>
      )}
    </div>
  );
}

// ============================================
// KPI Card Grid
// ============================================

interface KPIGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4 | 6;
}

export function KPIGrid({ children, columns = 4 }: KPIGridProps) {
  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-3 sm:gap-4`}>
      {children}
    </div>
  );
}
